import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { GridModule, ExcelModule } from '@progress/kendo-angular-grid';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { ToolBarModule } from '@progress/kendo-angular-toolbar';
import { MenuComponent } from './menu/menu.component';
import { GeneralHealthComponent } from './views/general-health/general-health.component';
import { ResultsComponent } from './shared/results/results.component';
import { IndicationFiltersComponent } from './search/indication-filters/indication-filters.component';
import { ResultsGridComponent } from './shared/results-grid/results-grid.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ToolbarComponent } from './search/toolbar/toolbar.component';
import { PreviousQueriesComponent } from './search/right-side-section/previous-queries/previous-queries.component';
import { CommonInputsComponent } from './search/common-inputs/common-inputs.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { NgxLoadingSpinnerComponent } from './shared/ngx-loading-spinner/ngx-loading-spinner.component';
import { ResultsChartsComponent } from './shared/results-charts/results-charts.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { QueryCardComponent } from '../app/search/right-side-section/query-card/query-card.component';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { RightSideSectionComponent } from './search/right-side-section/right-side-section.component';
import { NotFoundComponent } from './views/not-found/not-found.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { SidenavListComponent } from './menu/sidenav-list/sidenav-list.component';
import { SavedFiltersComponent } from './search/right-side-section/saved-filters/saved-filters.component';
import { AboutComponent } from './views/about/about.component';
import { DataSetsComponent } from './search/right-side-section/data-sets/data-sets.component';
import { JwtInterceptor } from './_helpers/jwt.interceptor';
import { appInitializer } from './_helpers/app.initializer';
import { AccountService } from './services/account-service';
import { ErrorInterceptor } from './_helpers/error.interceptor';
import { IndicationDefinitionComponent } from './shared/indication-definition/indication-definition.component';
import { CommonModule } from '@angular/common';
import { DiagnosedOnlyComponent } from './shared/diagnosed-only/diagnosed-only.component';
import { FilterSelectionsComponent } from './shared/filter-selections/filter-selections.component';
import { IndicationGroupComponent } from './search/indication-group/indication-group.component';
import { LimitsInputComponent } from './shared/inputs/limits-input/limits-input.component';
import { RadioInputGroupComponent } from './shared/inputs/radio-input-group/radio-input-group.component';
import { CheckboxTreeviewComponent } from './shared/inputs/checkbox-treeview/checkbox-treeview.component';
import { DyslipidemiaComponent } from './search/individual-indications/dyslipidemia/dyslipidemia.component';
import { HypertensionComponent } from './search/individual-indications/hypertension/hypertension.component';
import { DiabetesComponent } from './search/individual-indications/diabetes/diabetes.component';
import { AlbuminComponent } from './search/individual-indications/albumin/albumin.component';
import { InsomniaComponent } from './search/individual-indications/insomnia/insomnia.component';
import { TenYearRiskChdComponent } from './search/individual-indications/tenYearRiskChd/tenYearRiskChd.component';
import { MetabolicSyndromeComponent } from './search/individual-indications/metabolicSyndrome/metabolicSyndrome.component';
import { AgeComponent } from './search/individual-indications/age/age.component';
import { IconsModule } from '@progress/kendo-angular-icons';
import { SearchIndicationsComponent } from './search/search-indications/search-indications.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ValidationSummaryComponent } from './shared/validation-summary/validation-summary.component';
import { CkdComponent } from './search/individual-indications/ckd/ckd.component';
import { BodyWeightComponent } from './search/individual-indications/body-weight/body-weight.component';
import { HepatitisComponent } from './search/individual-indications/hepatitis/hepatitis.component';
import { AmchartChartComponent } from './shared/amchart-chart/amchart-chart.component';
import { SummarySectionComponent } from './shared/summary-section/summary-section.component';
import { AsthmaComponent } from './search/individual-indications/asthma/asthma.component';


@NgModule({
  declarations: [
    AppComponent,
    MenuComponent,
    GeneralHealthComponent,
    ResultsComponent,
    IndicationFiltersComponent,
    IndicationGroupComponent,
    ResultsGridComponent,
    ToolbarComponent,
    PreviousQueriesComponent,
    CommonInputsComponent,
    NgxLoadingSpinnerComponent,
    ResultsChartsComponent,
    QueryCardComponent,
    RightSideSectionComponent,
    NotFoundComponent,
    SidenavListComponent,
    SavedFiltersComponent,
    AboutComponent,
    RadioInputGroupComponent,
    LimitsInputComponent,
    CheckboxTreeviewComponent,
    FilterSelectionsComponent,
    DataSetsComponent,
    IndicationDefinitionComponent,
    DiagnosedOnlyComponent,
    DyslipidemiaComponent,
    HypertensionComponent,
    DiabetesComponent,
    AlbuminComponent,
    InsomniaComponent,
    TenYearRiskChdComponent,
    MetabolicSyndromeComponent,
    AgeComponent,
    CkdComponent,
    HepatitisComponent,
    BodyWeightComponent,
    AsthmaComponent,
    SearchIndicationsComponent,
    ValidationSummaryComponent,
    AmchartChartComponent,
    SummarySectionComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    LayoutModule,
    BrowserAnimationsModule,
    HammerModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    ButtonsModule,
    LabelModule,
    GridModule,
    DropDownsModule,
    InputsModule,
    DialogsModule,
    ExcelExportModule,
    ExcelModule,
    ToolBarModule,
    HttpClientModule,
    NgxSpinnerModule,
    HighchartsChartModule,
    ProgressBarModule,
    NotificationModule,
    TreeViewModule,
    ListViewModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatMenuModule,
    IconsModule,
    NgbModule
  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [AccountService] },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
